import { useState } from 'react'
import { Bell, Volume2, Mail, X, Check } from 'lucide-react'

export default function NotificationSettings({ isOpen, onClose, preferences, onUpdatePreferences }) {
  const [formData, setFormData] = useState(preferences || {
    desktopNotifications: true,
    emailNotifications: true,
    reminderMinutes: [15, 5],
    soundEnabled: true,
  })

  const [reminderInput, setReminderInput] = useState('')

  const handleToggle = (key) => {
    setFormData({
      ...formData,
      [key]: !formData[key],
    })
  }

  const handleAddReminder = () => {
    const minutes = parseInt(reminderInput)
    if (minutes > 0 && !formData.reminderMinutes.includes(minutes)) {
      setFormData({
        ...formData,
        reminderMinutes: [...formData.reminderMinutes, minutes].sort((a, b) => b - a),
      })
      setReminderInput('')
    }
  }

  const handleRemoveReminder = (minutes) => {
    setFormData({
      ...formData,
      reminderMinutes: formData.reminderMinutes.filter((m) => m !== minutes),
    })
  }

  const handleSave = () => {
    onUpdatePreferences(formData)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-lg border border-blue-500/20 max-w-md w-full shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700 sticky top-0 bg-slate-800">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Bell className="w-5 h-5 text-blue-400" />
            Notification Settings
          </h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Desktop Notifications */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-semibold text-slate-300 flex items-center gap-2">
                <span className="w-5 h-5 text-blue-400">🖥️</span>
                Desktop Notifications
              </label>
              <button
                onClick={() => handleToggle('desktopNotifications')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  formData.desktopNotifications ? 'bg-blue-600' : 'bg-slate-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formData.desktopNotifications ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            <p className="text-xs text-slate-400">Receive notifications on your desktop when meetings are approaching</p>
          </div>

          {/* Email Notifications */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-semibold text-slate-300 flex items-center gap-2">
                <Mail className="w-4 h-4 text-purple-400" />
                Email Notifications
              </label>
              <button
                onClick={() => handleToggle('emailNotifications')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  formData.emailNotifications ? 'bg-purple-600' : 'bg-slate-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formData.emailNotifications ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            <p className="text-xs text-slate-400">Receive email reminders for upcoming meetings</p>
          </div>

          {/* Sound Notifications */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-semibold text-slate-300 flex items-center gap-2">
                <Volume2 className="w-4 h-4 text-green-400" />
                Sound Notifications
              </label>
              <button
                onClick={() => handleToggle('soundEnabled')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  formData.soundEnabled ? 'bg-green-600' : 'bg-slate-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formData.soundEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            <p className="text-xs text-slate-400">Play a sound when notifications are sent</p>
          </div>

          {/* Reminder Times */}
          <div className="border-t border-slate-700 pt-6">
            <p className="text-sm font-semibold text-slate-300 mb-3">Reminder Times (minutes before)</p>
            <div className="space-y-2 mb-4">
              {formData.reminderMinutes.length === 0 ? (
                <p className="text-xs text-slate-400">No reminders configured</p>
              ) : (
                formData.reminderMinutes.map((minutes) => (
                  <div
                    key={minutes}
                    className="flex items-center justify-between bg-slate-700/50 rounded-lg p-2 border border-blue-500/20"
                  >
                    <span className="text-sm text-white font-medium">{minutes} minutes before</span>
                    <button
                      onClick={() => handleRemoveReminder(minutes)}
                      className="text-red-400 hover:text-red-300 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* Add New Reminder */}
            <div className="flex gap-2">
              <input
                type="number"
                min="1"
                max="1440"
                value={reminderInput}
                onChange={(e) => setReminderInput(e.target.value)}
                placeholder="Enter minutes"
                className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm placeholder-slate-400 focus:outline-none focus:border-blue-500"
              />
              <button
                onClick={handleAddReminder}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium transition-colors"
              >
                Add
              </button>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 text-xs text-blue-300">
            <p className="font-semibold mb-1">💡 Tip:</p>
            <p>You can customize notification preferences for each attendee individually when creating or editing meetings.</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 border-t border-slate-700 p-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium transition-colors flex items-center justify-center gap-2"
          >
            <Check className="w-4 h-4" />
            Save Settings
          </button>
        </div>
      </div>
    </div>
  )
}
